import {
  buildPath,
  type RouteBody,
  type RouteDefinition,
  type RouteMultipart,
  type RouteName,
  type RouteParams,
  type RouteQuery,
  type RouteResponse,
  type Routes,
  routes,
} from '@seans/contracts';
import type { z } from 'zod';
import { ApiError } from './errors.ts';
import type { HttpTransport, QueryValue } from './http.ts';

/** Routes whose body is a multipart form (a `file` part plus text fields). */
export type MultipartRouteName = {
  [N in RouteName]: Routes[N] extends { readonly multipart: z.ZodObject } ? N : never;
}[RouteName];
export type JsonRouteName = Exclude<RouteName, MultipartRouteName>;

type Defined<T, Key extends string, Value> = T extends undefined ? unknown : { readonly [K in Key]: Value };

/** Input of a JSON route, derived from its definition: only the parts the route declares. */
export type CallInput<N extends JsonRouteName> = Defined<
  RouteParams<Routes[N]>,
  'params',
  RouteParams<Routes[N]>
> &
  (RouteQuery<Routes[N]> extends undefined ? unknown : { readonly query?: RouteQuery<Routes[N]> }) &
  Defined<RouteBody<Routes[N]>, 'body', RouteBody<Routes[N]>> & { readonly signal?: AbortSignal };

export type UploadInput<N extends MultipartRouteName> = Defined<
  RouteParams<Routes[N]>,
  'params',
  RouteParams<Routes[N]>
> & {
  readonly fields: RouteMultipart<Routes[N]>;
  readonly file: File;
  readonly signal?: AbortSignal;
};

type RequiredKeys<T> = { [K in keyof T]-?: object extends Pick<T, K> ? never : K }[keyof T];
/** The input argument may be omitted when the route needs nothing (e.g. `getSession`). */
type CallArgs<N extends JsonRouteName> = [RequiredKeys<CallInput<N>>] extends [never]
  ? [input?: CallInput<N>]
  : [input: CallInput<N>];

export interface ApiClient {
  call<N extends JsonRouteName>(name: N, ...args: CallArgs<N>): Promise<RouteResponse<Routes[N]>>;
  upload<N extends MultipartRouteName>(name: N, input: UploadInput<N>): Promise<RouteResponse<Routes[N]>>;
}

/** File uploads get a longer budget than ordinary JSON calls. */
const UPLOAD_TIMEOUT_MS = 120_000;

interface LooseInput {
  readonly params?: Readonly<Record<string, string>>;
  readonly query?: Readonly<Record<string, QueryValue>>;
  readonly body?: unknown;
  readonly signal?: AbortSignal;
}

const parseResponse = (name: RouteName, route: RouteDefinition, body: unknown): unknown => {
  if (route.response === null) return undefined;
  const parsed = route.response.safeParse(body);
  if (!parsed.success) {
    console.error(`[api] ${name}: response does not match the contract`, parsed.error.issues);
    throw new ApiError('invalid_response', `${name}: response does not match the contract`, {
      details: parsed.error.issues,
    });
  }
  return parsed.data;
};

/**
 * Typed client over the contract route table: every path, method and response shape comes from
 * `routes`, responses are validated with the route's schema, failures arrive as `ApiError`.
 */
export const createApiClient = (transport: HttpTransport): ApiClient => {
  const call = async (name: RouteName, input: LooseInput = {}): Promise<unknown> => {
    const route: RouteDefinition = routes[name];
    const response = await transport({
      method: route.method,
      path: buildPath(route.path, input.params),
      query: input.query,
      json: input.body,
      signal: input.signal,
    });
    return parseResponse(name, route, response.body);
  };

  const upload = async (
    name: RouteName,
    input: LooseInput & { fields: Readonly<Record<string, string | undefined>>; file: File },
  ): Promise<unknown> => {
    const route: RouteDefinition = routes[name];
    const form = new FormData();
    for (const [key, value] of Object.entries(input.fields)) {
      if (value !== undefined) form.set(key, value);
    }
    // The binary part goes last so servers that stream multipart see the fields first.
    form.set('file', input.file, input.file.name);
    const response = await transport({
      method: route.method,
      path: buildPath(route.path, input.params),
      formData: form,
      signal: input.signal,
      timeoutMs: UPLOAD_TIMEOUT_MS,
    });
    return parseResponse(name, route, response.body);
  };

  // The route table is the single source of every shape; the generic signatures above are the
  // compile-time view of exactly what `parseResponse` validated at runtime.
  return { call, upload } as unknown as ApiClient;
};
